// RTL Direction Utilities
export const rtlUtils = {
  // Get the correct margin/padding class based on direction
  getSpacing: (property, size, direction = 'ltr') => {
    const isRtl = direction === 'rtl'
    
    const spacingMap = {
      'ml': isRtl ? `mr-${size}` : `ml-${size}`,
      'mr': isRtl ? `ml-${size}` : `mr-${size}`,
      'pl': isRtl ? `pr-${size}` : `pl-${size}`,
      'pr': isRtl ? `pl-${size}` : `pr-${size}`,
      'border-l': isRtl ? `border-r-${size}` : `border-l-${size}`,
      'border-r': isRtl ? `border-l-${size}` : `border-r-${size}`,
      'rounded-l': isRtl ? `rounded-r-${size}` : `rounded-l-${size}`,
      'rounded-r': isRtl ? `rounded-l-${size}` : `rounded-r-${size}`,
    }
    
    return spacingMap[property] || `${property}-${size}`
  },

  // Get text alignment class
  getTextAlign: (align, direction = 'ltr') => {
    const isRtl = direction === 'rtl'
    
    const alignMap = {
      'left': isRtl ? 'text-right' : 'text-left',
      'right': isRtl ? 'text-left' : 'text-right',
      'start': isRtl ? 'text-right' : 'text-left',
      'end': isRtl ? 'text-left' : 'text-right'
    }
    
    return alignMap[align] || align
  },

  // Get positioning class
  getPosition: (position, value, direction = 'ltr') => {
    const isRtl = direction === 'rtl'
    
    const positionMap = {
      'left': isRtl ? `right-${value}` : `left-${value}`,
      'right': isRtl ? `left-${value}` : `right-${value}`
    }
    
    return positionMap[position] || `${position}-${value}`
  },

  // Get flex direction class
  getFlexDirection: (direction = 'ltr', flexDir = 'row') => {
    const isRtl = direction === 'rtl'
    
    const flexMap = {
      'flex-row': isRtl ? 'flex-row-reverse' : 'flex-row',
      'flex-row-reverse': isRtl ? 'flex-row' : 'flex-row-reverse',
      'row': isRtl ? 'row-reverse' : 'row',
      'row-reverse': isRtl ? 'row' : 'row-reverse'
    }
    
    return flexMap[flexDir] || flexDir
  },

  // Transform array of classes to be RTL-aware
  transformClasses: (classes, direction = 'ltr') => {
    if (typeof classes === 'string') {
      classes = classes.split(' ')
    }
    
    if (!Array.isArray(classes)) {
      return classes
    }
    
    return classes.map(cls => {
      // Handle margin classes
      if (cls.match(/^m[lr]-/)) {
        const [, type, value] = cls.match(/^(m[lr])-(.+)$/) || []
        return rtlUtils.getSpacing(type, value, direction)
      }
      
      // Handle padding classes
      if (cls.match(/^p[lr]-/)) {
        const [, type, value] = cls.match(/^(p[lr])-(.+)$/) || []
        return rtlUtils.getSpacing(type, value, direction)
      }
      
      // Handle border classes
      if (cls.match(/^border-[lr]/)) {
        const [, type, value] = cls.match(/^(border-[lr])(?:-(.+))?$/) || []
        return rtlUtils.getSpacing(type, value || '', direction)
      }
      
      // Handle rounded classes
      if (cls.match(/^rounded-[lr]/)) {
        const [, type, value] = cls.match(/^(rounded-[lr])(?:-(.+))?$/) || []
        return rtlUtils.getSpacing(type, value || '', direction)
      }
      
      // Handle text alignment
      if (cls.match(/^text-(left|right|start|end)$/)) {
        const [, align] = cls.match(/^text-(.+)$/) || []
        return rtlUtils.getTextAlign(align, direction)
      }
      
      // Handle positioning
      if (cls.match(/^(left|right)-/)) {
        const [, pos, value] = cls.match(/^(left|right)-(.+)$/) || []
        return rtlUtils.getPosition(pos, value, direction)
      }
      
      // Handle flex direction
      if (cls.match(/^flex-row/)) {
        return rtlUtils.getFlexDirection(direction, cls)
      }
      
      return cls
    }).join(' ')
  }
}

export default rtlUtils